import { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';

/**
 * Returns the most recent event timestamp for each given collection by querying
 * the activityFeed collection using its existing composite index:
 *   activityFeed (childId ASC, collectionName ASC, loggedAt DESC)
 *
 * This is a single efficient document read per collection rather than a full
 * collection scan. Re-fetches on childId change and on page visibility (PWA resume).
 *
 * @param {string|null} childId
 * @param {string[]} collectionNames - stable array (serialised internally to avoid re-renders)
 * @returns {Object.<string, Date|null>} map of collectionName → most recent Date or null
 */
export function useLastLogged(childId, collectionNames) {
  const [lastDates, setLastDates] = useState({});
  const [visibleAt, setVisibleAt] = useState(0);

  // Re-fetch when the app comes back from background
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') setVisibleAt(Date.now());
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, []);

  // Serialise the array so the effect doesn't re-fire on every render
  const collectionsKey = collectionNames.join(',');

  useEffect(() => {
    if (!childId) { setLastDates({}); return; }
    let cancelled = false;

    Promise.all(
      collectionsKey.split(',').map(async (name) => {
        try {
          const q = query(
            collection(db, 'activityFeed'),
            where('childId', '==', childId),
            where('collectionName', '==', name),
            orderBy('loggedAt', 'desc'),
            limit(1)
          );
          const snap = await getDocs(q);
          if (snap.empty) return [name, null];

          const data = snap.docs[0].data();
          // Prefer eventTime (when the event happened) over loggedAt (when it was recorded)
          const raw = data.eventTime || data.loggedAt;
          const d = raw ? new Date(raw) : null;
          return [name, d && !isNaN(d.getTime()) ? d : null];
        } catch {
          return [name, null];
        }
      })
    ).then(entries => {
      if (!cancelled) setLastDates(Object.fromEntries(entries));
    });

    return () => { cancelled = true; };
  }, [childId, collectionsKey, visibleAt]);

  return lastDates;
}
